/**
 * 音频筛选选项管理器
 * 负责管理音频筛选选项，提供获取筛选等功能
 * @author 杨俊朗
 */
import { useToast } from "@/hooks/use-toast";
import i18n from "@/locales";
import { audioFilterOptions } from "@/services/audio-filter-options";
import { canUseLocalStorage } from "@/utils/canuseLocalStorage";
import { canUseWindow } from "@/utils/canuseWindow";
import { capitalize } from "@/utils/capitialize";
import { useEffect, useState } from "react";

// 筛选选项类型
interface FilterOption {
  label: string;
  value: string;
}

// 筛选选项数据类型
interface FilterOptionsData {
  [key: string]: FilterOption[];
}

// 筛选选项
export const VOICE_OPTIONS = [
  // { value: "all_voices", label: "All Voices" },
  // { value: "starred_voices", label: "Favorite Voices" },
  // { value: "recently_used", label: "Recent Voices" },
  { value: "my_voices", label: "My Voices" }, // 暂时只保留这一个
];

// 音频筛选选项管理器
export class CreationAudioFilterOptionsManager {
  private static instance: CreationAudioFilterOptionsManager;
  private readonly STORAGE_KEY = "MIUTA_AUDIO_FILTER_OPTIONS";
  private filterOptions: Record<string, FilterOptionsData> = {
    [i18n.language]: {},
  };

  private constructor() {
    // 由外部决定初始化时机
    // this.init();
  }

  public get defaultOption() {
    return {
      label: "All",
      value: "all",
    };
  }

  // 单例模式
  public static getInstance(): CreationAudioFilterOptionsManager {
    if (!CreationAudioFilterOptionsManager.instance) {
      CreationAudioFilterOptionsManager.instance = new CreationAudioFilterOptionsManager();
    }
    return CreationAudioFilterOptionsManager.instance;
  }

  // 初始化
  public async init(): Promise<void> {
    // 重新设置filterOptions 保证只缓存一种语言的筛选项，避免浪费缓存空间。
    this.update({
      [i18n.language]: {},
    });
    // 尝试从缓存中获取数据
    if (canUseLocalStorage()) {
      try {
        const cached = localStorage.getItem(this.STORAGE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.data[i18n.language]) {
            this.update({
              [i18n.language]: parsed.data[i18n.language],
            });
            // 后台同步数据
            this.fetch();
            return;
          }
        }
      } catch (e) {
        console.error("Failed to parse audio filter options data:", e);
      }
    }
    // 如果缓存中没有数据，尝试从服务器获取
    await this.fetch();
  }

  // 获取当前语言筛选选项
  public get(): FilterOptionsData {
    return this.filterOptions[i18n.language] || {};
  }

  // 更新筛选选项
  private update(filterOptions: Record<string, FilterOptionsData>): void {
    this.filterOptions = filterOptions;
    this.dispatch();
  }

  // 获取筛选选项数据
  private async fetch(): Promise<void> {
    console.log("fetch audio filter options");
    const data = await audioFilterOptions();
    this.update({
      [i18n.language]: this.sort(this.format(data)),
    });
    // 将筛选选项数据缓存到本地
    this.cache();
  }

  // 缓存筛选选项数据
  private cache(): void {
    if (canUseLocalStorage()) {
      try {
        const cachedData = {
          timestamp: Date.now(),
          data: this.filterOptions,
        };
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cachedData));
        console.log("cache audio filter options", cachedData);
      } catch (e) {
        console.error("Failed to cache audio filter options data:", e);
      }
    }
  }

  // 添加所有选项
  private format(filterOptions: Record<string, FilterOption[]>) {
    const options = Object.entries(filterOptions).map(([key, value]) => [
      key,
      [{ label: `All ${capitalize(key)}`, value: "all" }, ...value],
    ]);
    return Object.fromEntries(options);
  }

  // 排序筛选选项
  private sort(filterOptions: Record<string, FilterOption[]>) {
    const options = ["gender", "age", "languages"];
    const obj = {};
    for (const key of options) {
      if (filterOptions[key]) {
        obj[key] = filterOptions[key];
      }
    }
    return obj;
  }

  // 监听函数可监听筛选选项变化
  public listen(listener: (e: AudioFilterOptionsEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(this.STORAGE_KEY, listener);

      return () => {
        window.removeEventListener(this.STORAGE_KEY, listener);
      };
    }
  }

  // 触发监听回调
  private dispatch(): void {
    if (canUseWindow()) {
      const event = new CustomEvent<FilterOptionsData>(this.STORAGE_KEY, {
        detail: this.filterOptions[i18n.language],
      });
      window.dispatchEvent(event);
    }
  }
}

export const useAudioFilterOptions = () => {
  const [options, setOptions] = useState<FilterOptionsData>(
    creationAudioFilterOptionsManager.get()
  );
  const [loading, setLoading] = useState(false);
  const { showToastByCode } = useToast();

  useEffect(() => {
    return creationAudioFilterOptionsManager.listen((e) => {
      setOptions(e.detail);
    });
  }, []);

  // 初始化筛选数据
  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setLoading(true);
    try {
      await creationAudioFilterOptionsManager.init();
    } catch (error) {
      showToastByCode(error);
    }
    setLoading(false);
  };

  const getDefaultOption = () => {
    return creationAudioFilterOptionsManager.defaultOption;
  };

  return {
    options,
    loading,
    getDefaultOption,
  };
};

export const creationAudioFilterOptionsHooks = {
  useOptions: useAudioFilterOptions,
};

// 导出单例实例
export const creationAudioFilterOptionsManager =
  CreationAudioFilterOptionsManager.getInstance();
